import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { entities as entitiesApi } from "../services/api";
import { useApp } from "../context/AppContext";
import { toast } from "../components/Toast";
import { formatDateTime } from "../utils/dateUtils";
import {
  BackIcon,
  TrashIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DocumentIcon,
  RestoreIcon,
} from "../components/Icons";
import { ConfirmModal } from "../components/Modal";
import DataTable, { RowActions } from "../components/DataTable";

function Trash() {
  const navigate = useNavigate();
  const {
    trash,
    trashLoading,
    trashHasMore,
    refreshTrash,
    loadMoreTrash,
    restoreEntity,
    permanentDeleteEntity,
    emptyTrashAction,
  } = useApp();
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [totalTrashCount, setTotalTrashCount] = useState(0);

  const loadTrashCount = useCallback(async () => {
    try {
      const { data } = await entitiesApi.trashStats();
      setTotalTrashCount(data.totalCount || 0);
    } catch (err) {
      console.error("Failed to load trash count:", err);
    }
  }, []);

  useEffect(() => {
    refreshTrash();
    loadTrashCount();
  }, [refreshTrash, loadTrashCount]);

  const handleRestore = async (item, clearQrCode = false) => {
    try {
      await restoreEntity(item.id, item.entity_type, clearQrCode);
      loadTrashCount();
    } catch (err) {
      console.error("Failed to restore item:", err);
      if (err.response?.data?.code === "QR_CONFLICT") {
        const proceed = window.confirm(
          `QR code is now assigned to another item.\n\nDo you want to restore this item without the QR code?`,
        );
        if (proceed) {
          await handleRestore(item, true);
        }
      } else {
        toast.error(err.response?.data?.error || "Failed to restore item");
      }
    }
  };

  const handlePermanentDelete = async (item) => {
    try {
      await permanentDeleteEntity(item.id);
      setShowDeleteConfirm(null);
      loadTrashCount();
    } catch (err) {
      console.error("Failed to delete item:", err);
      toast.error("Failed to delete item");
    }
  };

  const handleEmptyTrash = async () => {
    try {
      await emptyTrashAction();
      setShowEmptyConfirm(false);
      loadTrashCount();
    } catch (err) {
      console.error("Failed to empty trash:", err);
      toast.error("Failed to empty trash");
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "product":
        return <ProductIcon size={18} />;
      case "container":
        return <ContainerIcon size={18} />;
      case "location":
        return <LocationIcon size={18} />;
      default:
        return <DocumentIcon size={18} />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case "product":
        return "badge-product";
      case "container":
        return "badge-container";
      case "location":
        return "badge-location";
      default:
        return "badge-product";
    }
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      const { data } = await entitiesApi.listTrash({ limit: -1 });
      setSelectedIds(data.map((item) => item.id));
    } catch (err) {
      console.error("Failed to select all:", err);
    }
  };

  const handleBulkRestore = async () => {
    const itemsToRestore = trash.filter((item) =>
      selectedIds.includes(item.id),
    );
    const results = await Promise.allSettled(
      itemsToRestore.map((item) => restoreEntity(item.id, item.entity_type)),
    );
    setSelectedIds([]);
    loadTrashCount();

    const failedNames = [];
    for (let i = 0; i < results.length; i++) {
      if (results[i].status === "rejected") {
        const err = results[i].reason;
        if (err?.response?.data?.code === "QR_CONFLICT") {
          // Reuse the single-item conflict prompt (restore without QR code)
          await handleRestore(itemsToRestore[i]);
        } else {
          console.error("Failed to restore item:", err);
          failedNames.push(itemsToRestore[i].name);
        }
      }
    }
    if (failedNames.length > 0) {
      toast.error(`Failed to restore: ${failedNames.join(", ")}`);
    }
  };

  const handleBulkPermanentDelete = async () => {
    try {
      await Promise.all(selectedIds.map((id) => permanentDeleteEntity(id)));
      setSelectedIds([]);
      setShowBulkDeleteConfirm(false);
      loadTrashCount();
    } catch (err) {
      console.error("Failed to delete some items:", err);
      toast.error("Failed to delete some items");
    }
  };

  // Column definitions
  const columns = [
    {
      field: "name",
      header: "Name",
      render: (_, item) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--spacing-sm)",
          }}
        >
          {getIcon(item.entity_type)} {item.name}
        </span>
      ),
      getSortValue: (item) => (item.name || "").toLowerCase(),
    },
    {
      field: "entity_type",
      header: "Type",
      render: (value) => (
        <span className={`badge ${getColor(value)}`}>{value}</span>
      ),
    },
    {
      field: "deleted_at",
      header: "Deleted",
      render: (value) => (
        <span style={{ color: "var(--text-secondary)" }}>
          {formatDateTime(value)}
        </span>
      ),
      getSortValue: (item) => new Date(item.deleted_at).getTime(),
    },
  ];

  // Row actions
  const rowActions = (item) => (
    <RowActions
      actions={[
        {
          label: "Restore",
          icon: RestoreIcon,
          onClick: () => handleRestore(item),
        },
        {
          label: "Delete Forever",
          icon: TrashIcon,
          onClick: () => setShowDeleteConfirm(item),
          variant: "danger",
        },
      ]}
    />
  );

  // Bulk actions
  const bulkActions = [
    {
      label: "Restore",
      icon: RestoreIcon,
      onClick: handleBulkRestore,
      variant: "btn-secondary",
    },
    {
      label: "Delete Forever",
      onClick: () => setShowBulkDeleteConfirm(true),
      variant: "btn-danger",
    },
  ];

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Trash</h1>
            <p className="header-subtitle">
              {totalTrashCount} item{totalTrashCount !== 1 ? "s" : ""} in trash
            </p>
          </div>
          {totalTrashCount > 0 && (
            <div className="header-actions-right">
              <button
                onClick={() => setShowEmptyConfirm(true)}
                className="btn btn-ghost"
                style={{ color: "var(--accent-danger)" }}
              >
                Empty
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        <DataTable
          data={trash}
          loading={trashLoading}
          columns={columns}
          defaultSortField="deleted_at"
          defaultSortDirection="desc"
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onSelectAllAcrossPages={handleSelectAllAcrossPages}
          totalItemsCount={totalTrashCount}
          rowActions={rowActions}
          bulkActions={bulkActions}
          hasMore={trashHasMore}
          onLoadMore={loadMoreTrash}
          emptyIcon={TrashIcon}
          emptyTitle="Trash is empty"
          emptySubtitle="Deleted items will appear here"
        />
      </div>

      {/* Empty Trash Confirmation Modal */}
      <ConfirmModal
        isOpen={showEmptyConfirm}
        onClose={() => setShowEmptyConfirm(false)}
        onConfirm={handleEmptyTrash}
        title="Empty Trash?"
        message={
          <>
            <p>
              Are you sure you want to permanently delete all {totalTrashCount}{" "}
              items in trash?
            </p>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              This action cannot be undone.
            </p>
          </>
        }
        confirmText="Empty Trash"
        confirmVariant="danger"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkPermanentDelete}
        title={`Permanently Delete ${selectedIds.length} items?`}
        message={
          <>
            <p>
              Are you sure you want to permanently delete {selectedIds.length}{" "}
              item{selectedIds.length > 1 ? "s" : ""}?
            </p>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>
              This action cannot be undone.
            </p>
          </>
        }
        confirmText="Delete Forever"
        confirmVariant="danger"
      />

      {/* Single Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => handlePermanentDelete(showDeleteConfirm)}
        title="Permanently Delete?"
        message={
          showDeleteConfirm && (
            <>
              <p>
                Are you sure you want to permanently delete{" "}
                <strong>{showDeleteConfirm.name}</strong>?
              </p>
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                This action cannot be undone.
              </p>
            </>
          )
        }
        confirmText="Delete Forever"
        confirmVariant="danger"
      />
    </div>
  );
}

export default Trash;
